import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  AccountBalanceWalletOutlined,
  Campaign,
  ExpandLess,
  ExpandMore,
  Image,
  Inventory,
  LockOpen,
  LockOutlined,
  Monitor,
  NoteAlt,
  Storefront,
  SwapVert,
} from '@mui/icons-material';
import DashboardCatalogContent from './DashboardCatalogContent';
import DenominationBreakdown from '../../components/pos/DenominationBreakdown';
import TransportDetailsDialog from '../../components/pos/TransportDetailsDialog';
import { cartQuantity } from '../../utils/cartSummary';

// Same threshold EwayBillGenerationService uses on the backend.
const EWAY_BILL_THRESHOLD = 50000;

export default function DashboardRightPanel(props) {
  const {
    actionButtons,
    actionButtonRefs,
    isCatalogView,
    catalogFocusIndex,
    setCatalogFocusIndex,
    catalogFocusZone,
    setCatalogFocusZone,
    selectedProduct,
    selectedTaxInfo,
    getProductStock,
    onOpenStockLookup,
    isMobile,
    selectedTab,
    setSelectedTab,
    categories,
    isLoadingCategories,
    scrollbarStyle,
    activeCategory,
    productsToShow,
    isLoadingCategoryProducts,
    categoryHasMore,
    isFetchingMoreCategoryProducts,
    loadMoreCategoryProducts,
    inStockOnly,
    setInStockOnly,
    handleAddToCart,
    cartAddDisabled,
    cartAddDisabledHint,
    setIsCatalogView,
    inExchangeWorkflow,
    orderNotes,
    setOrderNotes,
    handleNotesBlur,
    currentSession,
    setIsCashMovementModalOpen,
    onOpenCashOffice,
    cashInOutEnabled = true,
    cashOfficeIncoming,
    setIsCloseSessionModalOpen,
    setIsOpenSessionModalOpen,
    store,
    terminal,
    cart,
    currentOrderId,
    billExpanded,
    setBillExpanded,
    denomBreakdownExpanded,
    setDenomBreakdownExpanded,
    exchangeReturnTotal,
    exchangeNewTotal,
    exchangeDirection,
    exchangeNetAmount,
    gross_subtotal,
    subtotal,
    discount_total,
    tax_total,
    orderCartItems,
    bill_discount_id,
    bill_discount_name,
    bill_discount_amount,
    total_promo_savings,
    applied_promotions,
    bill_promotion_discount_amount,
    bill_promotion_name,
    displayTotal,
    handleCancelExchange,
    canCompleteExchange,
    exchangeBlockReason,
    sessionBlocked,
    setExchangeCheckoutOpen,
    handleDiscard,
    customerRequired,
    salespersonRequired,
    selectedCustomer,
    selectedSalesperson,
    checkoutBlocked,
    setIsCheckoutOpen,
    billingMode,
    handleAddExchangeNewItem,
    giftVoucherSlots,
  } = props;

  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  const [transportGateOpen, setTransportGateOpen] = useState(false);
  const [transportSavedForOrder, setTransportSavedForOrder] = useState(null);

  const needsTransportDetails = !!selectedCustomer?.gstin
    && displayTotal > EWAY_BILL_THRESHOLD
    && transportSavedForOrder !== currentOrderId;

  const handleCheckoutClick = () => {
    if (needsTransportDetails) {
      setTransportGateOpen(true);
    } else {
      setIsCheckoutOpen(true);
    }
  };

  const gross = parseFloat(gross_subtotal || 0);
  const sub = parseFloat(subtotal || 0);
  const disc = parseFloat(discount_total || 0);
  const tax = parseFloat(tax_total || 0);
  // discount_total already includes promotion savings, which get their own named
  // rows below — subtract them here so the generic row only covers manual/bill/
  // coupon discounts and the two never show the same rupees twice.
  const promoDiscount = parseFloat(total_promo_savings || 0) + parseFloat(bill_promotion_discount_amount || 0);
  const nonPromoDisc = Math.max(disc - promoDiscount, 0);
  const totalCgst = orderCartItems.reduce((s, i) => s + parseFloat(i.cgst_amount || 0), 0);
  const totalSgst = orderCartItems.reduce((s, i) => s + parseFloat(i.sgst_amount || 0), 0);
  const totalIgst = orderCartItems.reduce((s, i) => s + parseFloat(i.igst_amount || 0), 0);
  const isIntra = totalCgst > 0 || totalSgst > 0;

  return (
    <Box sx={{
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      width: { md: '42%', lg: '50%' },
      bgcolor: '#F8FAFC',
      overflow: 'hidden',
    }}>
      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
        {actionButtons.map((btn, i) => (
          <Button
            key={i}
            ref={(el) => { if (actionButtonRefs) actionButtonRefs.current[i] = el; }}
            variant="contained"
            disableElevation
            onClick={btn.onClick}
            disabled={btn.disabled}
            sx={{
              height: 48,
              textTransform: 'none',
              borderRadius: '8px',
              position: 'relative',
              justifyContent: 'flex-start',
              px: 1.25,
              minWidth: 0,
              overflow: 'hidden',
              bgcolor: btn.required ? '#FEF2F2' : '#FFF',
              color: '#1E293B',
              border: btn.required ? '1px solid #FECACA' : '1px solid #E2E8F0',
              '&:hover': { bgcolor: btn.required ? '#FEE2E2' : '#F1F5F9', borderColor: btn.color },
              '&.Mui-disabled': { bgcolor: '#FAFAFA', color: '#CBD5E1', border: '1px solid #F1F5F9' },
              '&:focus-visible': { bgcolor: '#EEF2FF', outline: '2px solid #4F46E5', outlineOffset: '1px' },
            }}
          >
            <Box sx={{ color: btn.disabled ? '#CBD5E1' : btn.color, display: 'flex', flexShrink: 0, mr: 0.75 }}>
              {btn.icon}
            </Box>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, flexGrow: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {btn.label}
            </Typography>
            {btn.required ? (
              <Box sx={{ bgcolor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '4px', px: 0.5, py: 0.15, ml: 0.5 }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#EF4444', lineHeight: 1.4 }}>REQ</Typography>
              </Box>
            ) : btn.shortcut ? (
              <Box sx={{ bgcolor: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '4px', px: 0.5, py: 0.15, ml: 0.5 }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#94A3B8', lineHeight: 1.4 }}>{btn.shortcut}</Typography>
              </Box>
            ) : null}
          </Button>
        ))}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Paper
          variant="outlined"
          sx={{
            p: { md: 1.5, lg: 2 },
            borderRadius: '8px',
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#FFF',
            border: !giftVoucherSlots && !isCatalogView && !selectedProduct ? '2px dashed #E2E8F0' : '1px solid #E2E8F0',
          }}
        >
          {giftVoucherSlots ? giftVoucherSlots.center : isCatalogView ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
              <DashboardCatalogContent
                isMobile={isMobile}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                categories={categories}
                isLoadingCategories={isLoadingCategories}
                scrollbarStyle={scrollbarStyle}
                activeCategory={activeCategory}
                productsToShow={productsToShow}
                isLoadingCategoryProducts={isLoadingCategoryProducts}
                categoryHasMore={categoryHasMore}
                isFetchingMoreCategoryProducts={isFetchingMoreCategoryProducts}
                loadMoreCategoryProducts={loadMoreCategoryProducts}
                inStockOnly={inStockOnly}
                setInStockOnly={setInStockOnly}
                handleAddToCart={inExchangeWorkflow ? handleAddExchangeNewItem : handleAddToCart}
                disabled={!inExchangeWorkflow && cartAddDisabled}
                disabledHint={!inExchangeWorkflow ? cartAddDisabledHint : ''}
                setIsCatalogView={setIsCatalogView}
                getProductStock={getProductStock}
                catalogFocusIndex={catalogFocusIndex}
                setCatalogFocusIndex={setCatalogFocusIndex}
                catalogFocusZone={catalogFocusZone}
                setCatalogFocusZone={setCatalogFocusZone}
              />
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {selectedProduct ? (
                <>
                  {/* ── Section label ── */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.09rem' }}>
                      PRODUCT INFORMATION
                    </Typography>
                    {onOpenStockLookup && (
                      <Tooltip title="Check stock at other stores &amp; warehouses">
                        <Button
                          size="small"
                          onClick={() => onOpenStockLookup(selectedProduct)}
                          startIcon={<Storefront sx={{ fontSize: '13px !important' }} />}
                          sx={{
                            minWidth: 0, height: 22, px: 0.75, py: 0, flexShrink: 0,
                            borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.6rem',
                            border: '1px solid #C7D2FE', color: '#4F46E5', bgcolor: '#EEF2FF',
                            '&:hover': { bgcolor: '#E0E7FF', borderColor: '#A5B4FC' },
                            '& .MuiButton-startIcon': { mr: 0.35 },
                          }}
                        >
                          Other Stores
                        </Button>
                      </Tooltip>
                    )}
                  </Box>

                  {/* ── Zone 1 · Identity ── */}
                  <Stack direction="row" spacing={{ md: 1.5, lg: 2 }} alignItems="flex-start" sx={{ mb: 1.5 }}>
                    {/* Thumbnail */}
                    <Box sx={{
                      width: { md: 58, lg: 72 }, height: { md: 58, lg: 72 },
                      borderRadius: '10px', bgcolor: '#F1F5F9',
                      border: '1px solid #E2E8F0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.07)',
                    }}>
                      {selectedProduct.image_url
                        ? <Box component="img" src={selectedProduct.image_url} alt={selectedProduct.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Image sx={{ color: '#CBD5E1', fontSize: { md: 22, lg: 28 } }} />}
                    </Box>

                    {/* Name · Brand · Category · SKU / HSN tags */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: { md: '0.92rem', lg: '1.05rem' }, fontWeight: 900,
                        color: '#0F172A', lineHeight: 1.2, mb: 0.35,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {selectedProduct.name}
                      </Typography>

                      {(selectedProduct.brand || selectedProduct.category?.name) && (
                        <Typography sx={{
                          fontSize: '0.67rem', color: '#64748B', fontWeight: 600,
                          mb: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {[selectedProduct.brand, selectedProduct.category?.name].filter(Boolean).join(' · ')}
                        </Typography>
                      )}

                      {/* Pill tags for SKU + HSN */}
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {[
                          { text: selectedProduct.sku },
                          selectedProduct.hsn_code?.code && { text: `HSN ${selectedProduct.hsn_code.code}` },
                        ].filter(Boolean).map(({ text }) => (
                          <Box key={text} sx={{
                            bgcolor: '#F1F5F9', border: '1px solid #E2E8F0',
                            borderRadius: '5px', px: 0.75, py: 0.2,
                          }}>
                            <Typography sx={{ fontSize: '0.57rem', color: '#64748B', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.02rem' }}>
                              {text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Stack>

                  {/* ── Zone 2 · Pricing ── */}
                  <Box sx={{
                    bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                    borderRadius: '12px', p: { md: 1.25, lg: 1.5 },
                    display: 'flex', alignItems: 'center',
                    gap: { md: 1.25, lg: 1.75 }, mb: 1.25,
                  }}>
                    {/* Hero retail price */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.08rem', mb: 0.3 }}>
                        RETAIL PRICE
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: { md: '1.1rem', lg: '1.3rem' }, fontWeight: 900, color: '#0F172A', lineHeight: 1 }}>
                          ₹{parseFloat(selectedProduct.retail_price).toFixed(2)}
                        </Typography>
                        {selectedProduct.mrp && parseFloat(selectedProduct.mrp) > parseFloat(selectedProduct.retail_price) && (
                          <Typography sx={{ fontSize: { md: '0.68rem', lg: '0.75rem' }, fontWeight: 600, color: '#94A3B8', textDecoration: 'line-through', lineHeight: 1, flexShrink: 0 }}>
                            ₹{parseFloat(selectedProduct.mrp).toFixed(2)}
                          </Typography>
                        )}
                        {selectedProduct.price_capped_at_mrp && (
                          <Chip label="MRP CAPPED" size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#FFFBEB', color: '#92400E', borderRadius: '4px', flexShrink: 0, '& .MuiChip-label': { px: 0.6 } }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontWeight: 600, mt: 0.3 }}>
                        {selectedProduct.price_inclusive_tax ? 'tax included' : 'excl. tax'}
                      </Typography>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '1px', height: 40, bgcolor: '#E2E8F0', flexShrink: 0 }} />

                    {/* Tax badge */}
                    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.07rem', mb: 0.45 }}>TAX</Typography>
                      <Box sx={{ bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', px: { md: 0.75, lg: 1.1 }, py: 0.4 }}>
                        <Typography sx={{ fontSize: { md: '0.78rem', lg: '0.88rem' }, fontWeight: 800, color: '#2563EB' }}>
                          {selectedTaxInfo.rate}%
                        </Typography>
                      </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '1px', height: 40, bgcolor: '#E2E8F0', flexShrink: 0 }} />

                    {/* Stock badge */}
                    {(() => {
                      const stock = getProductStock(selectedProduct);
                      const isLow = stock < 5;
                      return (
                        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.07rem', mb: 0.45 }}>STOCK</Typography>
                          <Box sx={{
                            bgcolor: isLow ? '#FEF2F2' : '#F0FDF4',
                            border: `1px solid ${isLow ? '#FECACA' : '#BBF7D0'}`,
                            borderRadius: '8px', px: { md: 0.75, lg: 1.1 }, py: 0.4, minWidth: 32,
                          }}>
                            <Typography sx={{ fontSize: { md: '0.78rem', lg: '0.88rem' }, fontWeight: 800, color: isLow ? '#DC2626' : '#16A34A' }}>
                              {stock}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* ── Zone 3 · Attributes ── */}
                  {(() => {
                    const attrs = [
                      selectedProduct.unit && { label: 'UNIT', value: selectedProduct.unit },
                      selectedProduct.color && { label: 'COLOR', value: selectedProduct.color },
                      selectedProduct.size && { label: 'SIZE', value: selectedProduct.size },
                    ].filter(Boolean);
                    if (attrs.length === 0 && !selectedProduct.description) return null;
                    return (
                      <Box>
                        {attrs.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, mb: selectedProduct.description ? 1 : 0 }}>
                            {attrs.map(({ label, value }) => (
                              <Box key={label} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.6,
                                bgcolor: '#FFFFFF', border: '1px solid #E2E8F0',
                                borderRadius: '20px', px: 1.1, py: 0.4,
                              }}>
                                <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.05rem' }}>
                                  {label}
                                </Typography>
                                <Box sx={{ width: '1px', height: 9, bgcolor: '#E2E8F0' }} />
                                <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#334155' }}>
                                  {value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {selectedProduct.description && (
                          <Typography sx={{ fontSize: '0.65rem', color: '#64748B', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {selectedProduct.description}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                  <Inventory sx={{ color: '#E2E8F0', fontSize: 32, mb: 1 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#CBD5E1', textAlign: 'center' }}>
                    {cart.length === 0 ? 'Add a product to see details' : 'Select a cart item to view details'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, flexShrink: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#FFF', borderRadius: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem' }}>SESSION</Typography>
            <Button
              size="small"
              onClick={(e) => setNoteAnchorEl(e.currentTarget)}
              startIcon={<NoteAlt sx={{ fontSize: '13px !important' }} />}
              sx={{ minWidth: 0, height: 22, px: 0.75, py: 0, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.6rem', border: '1px solid', color: orderNotes ? '#4F46E5' : '#64748B', borderColor: orderNotes ? '#C7D2FE' : '#E2E8F0', bgcolor: orderNotes ? '#EEF2FF' : '#FFF', '&:hover': { bgcolor: '#EEF2FF', borderColor: '#C7D2FE' }, '& .MuiButton-startIcon': { mr: 0.35 } }}
            >
              {orderNotes ? 'Note' : 'Add note'}
            </Button>
          </Box>

          <Popover
            open={Boolean(noteAnchorEl)}
            anchorEl={noteAnchorEl}
            onClose={() => { handleNotesBlur(); setNoteAnchorEl(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 1, mt: 0.5, width: 280, borderRadius: '10px', border: '1px solid #E2E8F0' } }}
          >
            <TextField
              fullWidth multiline minRows={2} maxRows={4} autoFocus
              placeholder="Add order note..."
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              onBlur={handleNotesBlur}
              InputProps={{ startAdornment: <NoteAlt sx={{ fontSize: 14, color: '#94A3B8', mr: 0.75, alignSelf: 'flex-start', mt: '6px' }} /> }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', borderRadius: '8px', bgcolor: '#FFF' }, '& .MuiOutlinedInput-input': { py: 0.4, px: 0.5 } }}
            />
          </Popover>

          {currentSession ? (
            <Stack spacing={0.6}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip
                  label={currentSession.status.toUpperCase()}
                  size="small"
                  sx={{
                    height: 17,
                    fontSize: '0.58rem',
                    fontWeight: 800,
                    bgcolor: currentSession.status === 'open' ? '#D1FAE5' : '#FEF3C7',
                    color: currentSession.status === 'open' ? '#065F46' : '#92400E',
                    borderRadius: '4px',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentSession.session_number}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <Storefront sx={{ fontSize: 12, color: '#64748B', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{store?.name}</Typography>
              </Stack>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <Monitor sx={{ fontSize: 12, color: '#64748B', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {terminal?.terminal_number} · {terminal?.name}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.75} alignItems="center">
                <AccessTime sx={{ fontSize: 12, color: '#64748B', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentSession.status === 'closed' && currentSession.closed_at
                    ? `Closed ${new Date(currentSession.closed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
                    : currentSession.opened_at
                      ? `Opened ${new Date(currentSession.opened_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
                      : '—'}
                </Typography>
              </Stack>

              <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>
                Opening: <span style={{ fontWeight: 700, color: '#1E293B' }}>₹{parseFloat(currentSession.opening_balance || 0).toFixed(2)}</span>
              </Typography>

              {/* Cash Office only: organizations without the module see the card exactly as before. */}
              {onOpenCashOffice && currentSession.status === 'open' && currentSession.summary?.expected_cash != null && (
                <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>
                  Expected now: <Box component="span" sx={{ fontWeight: 700, color: '#1E293B' }}>₹{parseFloat(currentSession.summary.expected_cash).toFixed(2)}</Box>
                </Typography>
              )}

              {currentSession.status === 'closed' && currentSession.closing_balance != null && (
                <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>
                  Closing: <span style={{ fontWeight: 700, color: '#1E293B' }}>₹{parseFloat(currentSession.closing_balance).toFixed(2)}</span>
                </Typography>
              )}

              <DenominationBreakdown
                openingDenominations={currentSession.opening_denominations}
                closingDenominations={currentSession.closing_denominations}
                expanded={denomBreakdownExpanded}
                onToggle={() => setDenomBreakdownExpanded((v) => !v)}
              />

              <Divider sx={{ my: 0.25 }} />

              {currentSession.status === 'open' ? (
                <Stack direction="row" spacing={0.75}>
                  {cashInOutEnabled && (
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    startIcon={<SwapVert sx={{ fontSize: '13px !important' }} />}
                    onClick={() => setIsCashMovementModalOpen(true)}
                    sx={{
                      fontSize: '0.6rem',
                      py: 0.4,
                      px: 0.75,
                      fontWeight: 700,
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: '#E2E8F0',
                      color: '#64748B',
                      '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                    }}
                  >
                    Cash Entry
                  </Button>
                  )}
                  {onOpenCashOffice && (
                    <Button
                      size="small"
                      fullWidth
                      variant="outlined"
                      startIcon={<AccountBalanceWalletOutlined sx={{ fontSize: '13px !important' }} />}
                      onClick={onOpenCashOffice}
                      aria-label={cashOfficeIncoming ? `Cash Office, ${cashOfficeIncoming} on the way` : 'Cash Office'}
                      sx={{
                        fontSize: '0.6rem',
                        py: 0.4,
                        px: 0.75,
                        fontWeight: 700,
                        borderRadius: '6px',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        borderColor: '#C7D2FE',
                        color: '#4F46E5',
                        bgcolor: '#EEF2FF',
                        '&:hover': { borderColor: '#4F46E5', bgcolor: '#EEF2FF' },
                      }}
                    >
                      Cash Office
                      {cashOfficeIncoming > 0 && (
                        <Box component="span" sx={{ ml: 0.5, bgcolor: '#4F46E5', color: '#fff', borderRadius: '8px', px: 0.6, fontSize: '0.55rem', lineHeight: '14px' }}>
                          {cashOfficeIncoming}
                        </Box>
                      )}
                    </Button>
                  )}
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    startIcon={<LockOutlined sx={{ fontSize: '13px !important' }} />}
                    onClick={() => setIsCloseSessionModalOpen(true)}
                    sx={{
                      fontSize: '0.6rem',
                      py: 0.4,
                      px: 0.75,
                      fontWeight: 700,
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: '#FECACA',
                      color: '#EF4444',
                      '&:hover': { borderColor: '#EF4444', bgcolor: '#FEF2F2' },
                    }}
                  >
                    Close
                  </Button>
                </Stack>
              ) : (
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  startIcon={<LockOpen sx={{ fontSize: '13px !important' }} />}
                  onClick={() => setIsOpenSessionModalOpen(true)}
                  disableElevation
                  sx={{
                    fontSize: '0.6rem',
                    py: 0.5,
                    fontWeight: 700,
                    borderRadius: '6px',
                    textTransform: 'none',
                    bgcolor: '#4F46E5',
                    color: '#fff',
                    '&:hover': { bgcolor: '#4338CA' },
                  }}
                >
                  Open New Session
                </Button>
              )}
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CircularProgress size={11} sx={{ color: '#94A3B8' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>Checking session...</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Storefront sx={{ fontSize: 12, color: '#CBD5E1' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#CBD5E1' }}>{store?.name}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Monitor sx={{ fontSize: 12, color: '#CBD5E1' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#CBD5E1' }}>
                  {terminal?.terminal_number} · {terminal?.name}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#FFF', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
          {giftVoucherSlots ? giftVoucherSlots.summary : (
          <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: inExchangeWorkflow ? '#6366F1' : '#94A3B8', letterSpacing: '0.05rem' }}>
                {inExchangeWorkflow ? 'EXCHANGE SUMMARY' : 'BILLING DETAILS'}
              </Typography>
              {!inExchangeWorkflow && cart.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '999px', bgcolor: '#DCFCE7', border: '1px solid #BBF7D0' }}>
                  {[[cart.length, cart.length === 1 ? 'item' : 'items'], [cartQuantity(cart), 'qty']].map(([count, label], i) => (
                    <React.Fragment key={label}>
                      {i > 0 && <Box sx={{ width: '1px', height: 10, bgcolor: '#86EFAC' }} />}
                      <Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#166534' }}>
                        <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 900, mr: 0.35 }}>{count}</Box>{label}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
            {!inExchangeWorkflow && currentOrderId && (
              <IconButton size="small" onClick={() => setBillExpanded((v) => !v)} sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#4F46E5' } }}>
                {billExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              </IconButton>
            )}
          </Box>

          {inExchangeWorkflow ? (
            <Box sx={{ flexGrow: 1 }}>
              {[
                { label: 'Return Credit', value: exchangeReturnTotal, color: '#C2410C', prefix: '−' },
                { label: 'New Items', value: exchangeNewTotal, color: '#4F46E5', prefix: '+' },
              ].map(({ label, value, color, prefix }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography sx={{ fontSize: '0.68rem', color }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color }}>{prefix}₹{value.toFixed(2)}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.6 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.72rem', color: exchangeDirection === 'customer_pays' ? '#1D4ED8' : exchangeDirection === 'customer_refund' ? '#7C3AED' : '#059669' }}>
                  {exchangeDirection === 'customer_pays' ? 'CUSTOMER PAYS' : exchangeDirection === 'customer_refund' ? 'CUSTOMER REFUND' : 'EVEN EXCHANGE'}
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: { md: '1.1rem', lg: '1.3rem' }, color: exchangeDirection === 'customer_pays' ? '#1D4ED8' : exchangeDirection === 'customer_refund' ? '#7C3AED' : '#059669' }}>
                  {exchangeDirection === 'even' ? '₹0' : `₹${Math.abs(exchangeNetAmount).toFixed(2)}`}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <Row label="Subtotal" value={gross} />
              {nonPromoDisc > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#10B981' }}>Discount</Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#10B981' }}>−₹{nonPromoDisc.toFixed(2)}</Typography>
                </Box>
              )}
              {billExpanded && bill_discount_id && (
                <Box sx={{ pl: 1.25, borderLeft: '2px solid #D1FAE5', ml: 0.25, mb: 0.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#6EE7B7' }}>↳ {bill_discount_name}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#10B981' }}>−₹{parseFloat(bill_discount_amount || 0).toFixed(2)}</Typography>
                  </Box>
                </Box>
              )}
              {parseFloat(total_promo_savings || 0) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#7C3AED', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Campaign sx={{ fontSize: 11 }} />
                    {applied_promotions.find((p) => p.applicable_level === 'line_item')?.promotion_name || 'Item Promo'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#7C3AED' }}>−₹{parseFloat(total_promo_savings).toFixed(2)}</Typography>
                </Box>
              )}
              {parseFloat(bill_promotion_discount_amount || 0) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#7C3AED', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Campaign sx={{ fontSize: 11 }} />
                    {bill_promotion_name || 'Bill Promo'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#7C3AED' }}>−₹{parseFloat(bill_promotion_discount_amount).toFixed(2)}</Typography>
                </Box>
              )}
              <Row label="Taxable Amount" value={sub} />
              <Row label="Tax (GST)" value={tax} />
              {billExpanded && currentOrderId && tax > 0 && (
                <Box sx={{ pl: 1.25, borderLeft: '2px solid #EEF2FF', ml: 0.25, mb: 0.25 }}>
                  {isIntra ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ CGST</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{totalCgst.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ SGST</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{totalSgst.toFixed(2)}</Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ IGST</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{(totalIgst || tax).toFixed(2)}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {!inExchangeWorkflow && (
            <Box sx={{ flexShrink: 0 }}>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.75rem' }}>TOTAL DUE</Typography>
                <Typography sx={{ fontWeight: 900, color: '#4F46E5', fontSize: { md: '1.2rem', lg: '1.4rem' } }}>₹{displayTotal.toFixed(2)}</Typography>
              </Box>
            </Box>
          )}
          </>
          )}
        </Paper>
      </Box>

      <Box sx={{ p: 1.5, pt: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, flexShrink: 0 }}>
        {giftVoucherSlots ? giftVoucherSlots.buttons : inExchangeWorkflow ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancelExchange}
              sx={{ height: 52, color: '#64748B', borderColor: '#E2E8F0', fontWeight: 700, borderRadius: '8px', '&:hover': { bgcolor: '#F8FAFC' } }}
            >
              Cancel
            </Button>
            <Tooltip
              title={exchangeBlockReason}
              placement="top"
              arrow
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={!canCompleteExchange || sessionBlocked}
                  onClick={() => setExchangeCheckoutOpen(true)}
                  sx={{ height: 52, bgcolor: '#6366F1', fontWeight: 900, borderRadius: '8px', fontSize: '0.82rem', letterSpacing: '0.02rem', '&:hover': { bgcolor: '#4F46E5' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }, width: '100%' }}
                >
                  COMPLETE EXCHANGE
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleDiscard}
              disabled={billingMode === 'return'}
              sx={{ height: 52, color: '#EF4444', borderColor: '#EF4444', fontWeight: 800, borderRadius: '8px', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}
            >
              DISCARD
            </Button>
            <Tooltip
              title={
                sessionBlocked && cart.length > 0 ? 'Open a cash session to continue billing'
                : customerRequired && !selectedCustomer && cart.length > 0 ? 'Customer required — press F2 to add'
                : salespersonRequired && !selectedSalesperson && cart.length > 0 ? 'Salesperson required — press F6 to add'
                : needsTransportDetails ? 'Transport details required for e-way bill'
                : ''
              }
              placement="top"
              arrow
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={checkoutBlocked || billingMode === 'exchange' || billingMode === 'return'}
                  onClick={handleCheckoutClick}
                  sx={{ height: 52, bgcolor: '#4F46E5', fontWeight: 900, borderRadius: '8px', fontSize: '0.9rem', letterSpacing: '0.03rem', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }, gap: 1, width: '100%' }}
                >
                  CHECKOUT
                  <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '4px', px: 0.6, py: 0.15 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, lineHeight: 1.4 }}>F7</Typography>
                  </Box>
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <TransportDetailsDialog
        open={transportGateOpen}
        orderId={currentOrderId}
        grandTotal={displayTotal}
        onClose={() => setTransportGateOpen(false)}
        onSaved={() => {
          setTransportSavedForOrder(currentOrderId);
          setTransportGateOpen(false);
          setIsCheckoutOpen(true);
        }}
      />
    </Box>
  );
}

function Row({ label, value, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
      <Typography sx={{ fontSize: '0.68rem', color: color || '#94A3B8' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: color || '#64748B' }}>
        ₹{parseFloat(value || 0).toFixed(2)}
      </Typography>
    </Box>
  );
}
